import { ElLoading } from 'element-plus'
import { deserialize } from '@/utils/tool'
import { defineStore } from 'pinia'
import { http, user } from '@/common/basis/root-injector'
import { ref, watch } from 'vue'
import { CmRolePermKey } from '@/enum/role-perm'

export interface RolePermsRes {
  list?: string[]
}

export type RolePermMapFe = {
  [key in keyof typeof CmRolePermKey]: boolean
}

export type RolePermMapBe = {
  [key in CmRolePermKey]: boolean
}

const ROLE_PERM_STORAGE_KEY = 'ROLE_PERMS'
const storageRolePerm = deserialize(localStorage.getItem(ROLE_PERM_STORAGE_KEY)) // array or null

export const useRolePermStore = defineStore('role-perm', () => {
  const currentRolePerms = ref<string[]>(storageRolePerm || [])
  // 前端key名权限映射
  const cmRolePermMapFe = ref<RolePermMapFe>({} as RolePermMapFe)
  // 后端key名权限映射
  const cmRolePermMapBe = ref<RolePermMapBe>({} as RolePermMapBe)
  const isLoading = ref(false)

  /**
   * 初始化当前用户的团队角色权限
   * @param loadMask 为true时，刷新权限时有loading遮罩层
   */
  function initCurrentRolePerms (loadMask?: boolean) {
    if (!user?.hasUser()) return

    let loadingInstance
    if (loadMask) {
      isLoading.value = true
      loadingInstance = ElLoading.service({ lock: true, fullscreen: true, background: 'var(--cm-color-loading-mask)' })
    }

    updateCurrentRolePerms().finally(() => {
      isLoading.value = false
      loadingInstance?.close()
    })
  }

  function updateCurrentRolePerms () {
    return getRemoteCurrentRolePerms().then((res) => {
      currentRolePerms.value = res.list.filter(v => !!v) || []
      localStorage.setItem(ROLE_PERM_STORAGE_KEY, JSON.stringify(currentRolePerms.value))
    }).catch(error => {
      const ret = error.responseBody?.ret // 取消http全局错误事件
    })
  }

  function getRemoteCurrentRolePerms () {
    return http.post<RolePermsRes>('/menu/user_perms_list', {})
  }

  function clearCurrentRolePerms () {
    currentRolePerms.value = []
    localStorage.removeItem(ROLE_PERM_STORAGE_KEY)
  }

  initCurrentRolePerms(true)

  watch(currentRolePerms, () => {
    Object.keys(CmRolePermKey).forEach((key) => {
      const hasPerm = currentRolePerms.value.includes(CmRolePermKey[key])
      cmRolePermMapFe.value[key] = hasPerm
      cmRolePermMapBe.value[CmRolePermKey[key]] = hasPerm
    })
  }, { immediate: true })

  return {
    isLoading,
    /** 前端key名权限映射 */
    cmRolePermMapFe,
    /** 后端key名权限映射 */
    cmRolePermMapBe,
    initCurrentRolePerms,
    clearCurrentRolePerms,
  }
})
